import React, { useCallback, useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import classNames from "classnames/bind";

import { useGetDashboardStatsQuery, useGetEnrollmentActivityQuery, useGetCategoryDistributionQuery, useGetTopCoursesQuery, useGetActivityStatsQuery } from "../../../api/adminStatsApi";
import { useGetAiCostsSummaryQuery } from "../../../api/aiCostsApi";
import { useGetAllReviewsQuery } from "../../../api/reviewsApi";
import { useSyncContentMutation, usePreviewContentSyncMutation } from "../../../api/contentSyncApi";
import type { ContentSyncResult, ContentSyncPreviewResult, ContentSyncPreviewDirection, ContentSyncPreviewGroup } from "@nicsmith/shared_types";
import { AdminDashboardSkeleton } from "../../common/skeleton";
import NotCriticalError from "../../common/not-critical-error";
import VisitorsCard from "./VisitorsCard";

import styles from "./AdminDashboardPage.module.scss";
import { chartSeries } from "../../../styles/tokens";

const cx = classNames.bind(styles);

// Icons
const BookIcon = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
    <path d="M4 19.5A2.5 2.5 0 0 1 6.5 17H20" />
    <path d="M6.5 2H20v20H6.5A2.5 2.5 0 0 1 4 19.5v-15A2.5 2.5 0 0 1 6.5 2z" />
  </svg>
);

const ClipboardIcon = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
    <path d="M16 4h2a2 2 0 0 1 2 2v14a2 2 0 0 1-2 2H6a2 2 0 0 1-2-2V6a2 2 0 0 1 2-2h2" />
    <rect x="8" y="2" width="8" height="4" rx="1" ry="1" />
  </svg>
);

const ThumbsUpIcon = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
    <path d="M14 9V5a3 3 0 0 0-3-3l-4 9v11h11.28a2 2 0 0 0 2-1.7l1.38-9a2 2 0 0 0-2-2.3zM7 22H4a2 2 0 0 1-2-2v-7a2 2 0 0 1 2-2h3" />
  </svg>
);

const UsersIcon = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
    <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2" />
    <circle cx="9" cy="7" r="4" />
    <path d="M23 21v-2a4 4 0 0 0-3-3.87" />
    <path d="M16 3.13a4 4 0 0 1 0 7.75" />
  </svg>
);

const UserPlusIcon = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
    <path d="M16 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2" />
    <circle cx="8.5" cy="7" r="4" />
    <line x1="20" y1="8" x2="20" y2="14" />
    <line x1="23" y1="11" x2="17" y2="11" />
  </svg>
);

const ActivityIcon = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
    <polyline points="22 12 18 12 15 21 9 3 6 12 2 12" />
  </svg>
);

const DotsIcon = () => (
  <svg viewBox="0 0 24 24" fill="currentColor">
    <circle cx="12" cy="5" r="2" />
    <circle cx="12" cy="12" r="2" />
    <circle cx="12" cy="19" r="2" />
  </svg>
);

const StarIcon = () => (
  <svg viewBox="0 0 24 24" fill="currentColor" stroke="currentColor" strokeWidth="1">
    <polygon points="12 2 15.09 8.26 22 9.27 17 14.14 18.18 21.02 12 17.77 5.82 21.02 7 14.14 2 9.27 8.91 8.26 12 2" />
  </svg>
);

const DollarIcon = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
    <line x1="12" y1="1" x2="12" y2="23" />
    <path d="M17 5H9.5a3.5 3.5 0 0 0 0 7h5a3.5 3.5 0 0 1 0 7H6" />
  </svg>
);

const ChevronRightIcon = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
    <polyline points="9 18 15 12 9 6" />
  </svg>
);

// Activity stat icons
const PencilIcon = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
    <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7" />
    <path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z" />
  </svg>
);

const CheckCircleIcon = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
    <path d="M22 11.08V12a10 10 0 1 1-5.93-9.14" />
    <polyline points="22 4 12 14.01 9 11.01" />
  </svg>
);

const BookOpenIcon = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
    <path d="M2 3h6a4 4 0 0 1 4 4v14a3 3 0 0 0-3-3H2z" />
    <path d="M22 3h-6a4 4 0 0 0-4 4v14a3 3 0 0 1 3-3h7z" />
  </svg>
);

const ZapIcon = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
    <polygon points="13 2 3 14 12 14 11 22 21 10 12 10 13 2" />
  </svg>
);

const AwardIcon = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
    <circle cx="12" cy="8" r="7" />
    <polyline points="8.21 13.89 7 23 12 20 17 23 15.79 13.88" />
  </svg>
);

const MessageSquareIcon = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
    <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z" />
  </svg>
);

const TrendUpIcon = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" width="14" height="14">
    <polyline points="23 6 13.5 15.5 8.5 10.5 1 18" />
    <polyline points="17 6 23 6 23 12" />
  </svg>
);

const TrendDownIcon = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" width="14" height="14">
    <polyline points="23 18 13.5 8.5 8.5 13.5 1 6" />
    <polyline points="17 18 23 18 23 12" />
  </svg>
);

// ============================================================================
// Sync Preview Modal
// ============================================================================

const PreviewGroupTable: React.FC<{
  title: string;
  group: ContentSyncPreviewGroup;
  t: (...args: any[]) => any;
}> = ({ title, group, t }) => {
  const total = group.add.length + group.update.length + group.delete.length;
  if (total === 0) return null;

  return (
    <div className={cx("previewGroup")}>
      <h5 className={cx("previewGroupTitle")}>{title} ({total})</h5>
      {group.add.length > 0 && (
        <div className={cx("previewSection")}>
          <span className={cx("previewBadge", "add")}>+{group.add.length} {t("adminDashboard.syncAdded", "Added")}</span>
          <table className={cx("previewTable")}>
            <tbody>
              {group.add.map((item, i) => (
                <tr key={`add-${i}`}>
                  <td className={cx("previewName")}>{item.name}</td>
                  <td className={cx("previewId")}>#{String(item.id)}</td>
                  <td className={cx("previewTime")}>{item.updated_at ? new Date(item.updated_at).toLocaleDateString() : "-"}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
      {group.update.length > 0 && (
        <div className={cx("previewSection")}>
          <span className={cx("previewBadge", "update")}>{group.update.length} {t("adminDashboard.syncUpdated", "Updated")}</span>
          <table className={cx("previewTable")}>
            <tbody>
              {group.update.map((item, i) => (
                <tr key={`upd-${i}`}>
                  <td className={cx("previewName")}>{item.name}</td>
                  <td className={cx("previewId")}>#{String(item.id)}</td>
                  <td className={cx("previewTime")}>{item.updated_at ? new Date(item.updated_at).toLocaleDateString() : "-"}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
      {group.delete.length > 0 && (
        <div className={cx("previewSection")}>
          <span className={cx("previewBadge", "delete")}>-{group.delete.length} {t("adminDashboard.syncDeleted", "Deleted")}</span>
          <table className={cx("previewTable")}>
            <tbody>
              {group.delete.map((item, i) => (
                <tr key={`del-${i}`}>
                  <td className={cx("previewName")}>{item.name}</td>
                  <td className={cx("previewId")}>#{String(item.id)}</td>
                  <td className={cx("previewTime")}>{item.updated_at ? new Date(item.updated_at).toLocaleDateString() : "-"}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
};

const PreviewDirectionColumn: React.FC<{
  title: string;
  direction: ContentSyncPreviewDirection;
  t: (...args: any[]) => any;
}> = ({ title, direction, t }) => {
  const totalItems = (["courses", "modules", "exercises", "categories", "vocabWords"] as const)
    .reduce((sum, key) => sum + direction[key].add.length + direction[key].update.length + direction[key].delete.length, 0);

  return (
    <div className={cx("previewColumn")}>
      <h4 className={cx("previewColumnTitle")}>{title} ({totalItems})</h4>
      {totalItems === 0 ? (
        <p className={cx("previewEmpty")}>{t("adminDashboard.syncNoChanges", "No changes")}</p>
      ) : (
        <>
          <PreviewGroupTable title={t("adminDashboard.courses", "Courses")} group={direction.courses} t={t} />
          <PreviewGroupTable title={t("adminDashboard.modules", "Modules")} group={direction.modules} t={t} />
          <PreviewGroupTable title={t("adminDashboard.exercises", "Exercises")} group={direction.exercises} t={t} />
          <PreviewGroupTable title={t("adminDashboard.categories", "Categories")} group={direction.categories} t={t} />
          <PreviewGroupTable title={t("adminDashboard.vocabWords", "Vocab Words")} group={direction.vocabWords} t={t} />
        </>
      )}
    </div>
  );
};

const SyncPreviewModal: React.FC<{
  preview: ContentSyncPreviewResult;
  onConfirm: () => void;
  onCancel: () => void;
  syncing: boolean;
  t: (...args: any[]) => any;
}> = ({ preview, onConfirm, onCancel, syncing, t }) => {
  return (
    <div className={cx("previewOverlay")} onClick={onCancel}>
      <div className={cx("previewModal")} onClick={e => e.stopPropagation()}>
        <div className={cx("previewHeader")}>
          <h3 className={cx("previewTitle")}>{t("adminDashboard.syncPreview", "Sync Preview")}</h3>
          <button className={cx("previewClose")} onClick={onCancel}>&times;</button>
        </div>

        <div className={cx("previewContent")}>
          <div className={cx("previewColumns")}>
            <PreviewDirectionColumn title={t("adminDashboard.syncDevToProd", "Dev \u2192 Prod")} direction={preview.toProd} t={t} />
            <PreviewDirectionColumn title={t("adminDashboard.syncProdToDev", "Prod \u2192 Dev")} direction={preview.toDev} t={t} />
          </div>

          {preview.duplicatesResolved.length > 0 && (
            <div className={cx("previewDuplicates")}>
              <h4 className={cx("previewDuplicatesTitle")}>{t("adminDashboard.syncDuplicatesResolved", "Duplicates Resolved")} ({preview.duplicatesResolved.length})</h4>
              <table className={cx("previewTable")}>
                <thead>
                  <tr>
                    <th>{t("adminUserDetails.type", "Type")}</th>
                    <th>{t("adminDashboard.syncSlug", "Slug")}</th>
                    <th>{t("adminDashboard.syncDevId", "Dev ID")}</th>
                    <th>{t("adminDashboard.syncProdId", "Prod ID")}</th>
                    <th>{t("adminDashboard.syncWinner", "Winner")}</th>
                  </tr>
                </thead>
                <tbody>
                  {preview.duplicatesResolved.map((dup, i) => (
                    <tr key={i}>
                      <td>{dup.type}</td>
                      <td>{dup.slug}</td>
                      <td>#{String(dup.devId)}</td>
                      <td>#{String(dup.prodId)}</td>
                      <td className={cx("previewWinner", dup.winner)}>{dup.winner}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>

        <div className={cx("previewFooter")}>
          <button className={cx("syncButton", "cancel")} onClick={onCancel} disabled={syncing}>{t("common.cancel", "Cancel")}</button>
          <button className={cx("syncButton")} onClick={onConfirm} disabled={syncing}>
            {syncing ? t("adminDashboard.syncing", "Syncing...") : t("adminDashboard.syncConfirm", "Confirm Sync")}
          </button>
        </div>
      </div>
    </div>
  );
};

const AdminDashboardPage: React.FC = () => {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const [period, setPeriod] = useState<"day" | "week" | "month">("week");

  // Format chart labels using the ISO date from `period` field, localized to user's language
  const formatChartLabel = useCallback((isoDate: string, periodType: "day" | "week" | "month") => {
    const date = new Date(isoDate + "T00:00:00");
    if (isNaN(date.getTime())) return isoDate; // fallback to raw label if parsing fails
    const locale = i18n.language;
    if (periodType === "day") {
      return date.toLocaleDateString(locale, { weekday: "short" });
    } else if (periodType === "week") {
      return date.toLocaleDateString(locale, { month: "short", day: "numeric" });
    } else {
      return date.toLocaleDateString(locale, { month: "short" });
    }
  }, [i18n.language]);

  // Fetch dashboard data
  const { data: stats, isLoading: statsLoading, error: statsError } = useGetDashboardStatsQuery();
  const { data: enrollmentActivity, isLoading: activityLoading } = useGetEnrollmentActivityQuery({ period });
  const { data: categoryDistribution, isLoading: categoriesLoading } = useGetCategoryDistributionQuery({ lang: i18n.language });
  const { data: topCourses, isLoading: coursesLoading } = useGetTopCoursesQuery({ limit: 2, lang: i18n.language });
  const { data: reviews, isLoading: reviewsLoading } = useGetAllReviewsQuery({ limit: 3 });
  const { data: aiCostsSummary, isLoading: aiCostsLoading } = useGetAiCostsSummaryQuery();
  const { data: activityStats, isLoading: activityStatsLoading } = useGetActivityStatsQuery({ period });

  // Content sync
  const [syncContent, { isLoading: syncing }] = useSyncContentMutation();
  const [previewSync, { isLoading: previewing }] = usePreviewContentSyncMutation();
  const [syncOpts, setSyncOpts] = useState({ content: true, translations: false, media: false });
  const [syncResult, setSyncResult] = useState<ContentSyncResult | null>(null);
  const [previewResult, setPreviewResult] = useState<ContentSyncPreviewResult | null>(null);
  const [syncExpanded, setSyncExpanded] = useState(false);

  const handleSync = async () => {
    try {
      const result = await syncContent(syncOpts).unwrap();
      setSyncResult(result);
      setPreviewResult(null);
    } catch {
      // error handled by baseQuery toast
    }
  };

  const handlePreview = async () => {
    try {
      const result = await previewSync(syncOpts).unwrap();
      setPreviewResult(result);
      setSyncResult(null);
    } catch {
      // error handled by baseQuery toast
    }
  };

  const handleConfirmSync = async () => {
    setPreviewResult(null);
    await handleSync();
  };

  const handleCancelPreview = () => {
    setPreviewResult(null);
  };

  const isLoading = statsLoading || activityLoading || categoriesLoading || coursesLoading || reviewsLoading || aiCostsLoading;
  const hasError = statsError;

  if (isLoading) {
    return (
      <div className={cx("adminDashboard")}>
        <AdminDashboardSkeleton />
      </div>
    );
  }

  if (hasError) {
    return (
      <div className={cx("adminDashboard")}>
        <div className={cx("errorState")}>
          <NotCriticalError />
        </div>
      </div>
    );
  }

  // Use real data or defaults
  const totalCourses = stats?.total_courses || 0;
  const totalModules = stats?.total_modules || 0;
  const totalReviews = stats?.total_reviews || 0;
  const totalUsers = stats?.total_users || 0;
  const totalStudents = stats?.total_students || 0;
  const newUsers30d = stats?.new_users_last_30_days || 0;
  const activeUsers7d = stats?.active_users_last_7_days || 0;

  // Stat card definitions
  const statCards = [
    { color: "green", icon: <BookIcon />, value: totalCourses.toLocaleString(i18n.language), label: t("adminDashboard.totalCourses", "Total Courses"), to: "/admin/courses" },
    { color: "yellow", icon: <ClipboardIcon />, value: totalModules.toLocaleString(i18n.language), label: t("adminDashboard.coursesContent", "Course Modules"), to: "/admin/courses" },
    { color: "coral", icon: <ThumbsUpIcon />, value: totalReviews.toLocaleString(i18n.language), label: t("adminDashboard.totalReviews", "Total Reviews"), to: null },
    { color: "purple", icon: <UsersIcon />, value: t("studentsPage.usersCount", { count: totalUsers }), subtitle: t("studentsPage.studentsCount", { count: totalStudents }), label: t("adminDashboard.totalStudents", "Total Learners"), to: "/admin/users" },
    { color: "blue", icon: <UserPlusIcon />, value: newUsers30d.toLocaleString(i18n.language), label: t("adminDashboard.newUsers30d", "New Users (30d)"), to: "/admin/users" },
    { color: "teal", icon: <ActivityIcon />, value: activeUsers7d.toLocaleString(i18n.language), label: t("adminDashboard.activeUsers", "Active Users"), to: "/admin/users" },
    { color: "coral", icon: <DollarIcon />, value: `$${(aiCostsSummary?.total_cost_usd || 0).toFixed(2)}`, label: t("adminDashboard.aiCostsMonth", "AI Costs (Month)"), to: "/admin/ai-costs" },
  ];

  // Enrollment activity chart data
  const chartData = enrollmentActivity?.current_period || [];
  const prevChartData = enrollmentActivity?.previous_period || [];
  const maxBarValue = Math.max(
    ...chartData.map(d => d.count),
    ...prevChartData.map(d => d.count),
    1
  ) * 1.1;

  // Category distribution
  const categories = categoryDistribution?.items || [];
  const categoryColors = ["yellow", "coral", "green", "purple", "blue"] as const;

  // Reviews for display
  const displayReviews = (reviews || []).slice(0, 3).map(review => ({
    id: review.id,
    name: review.user_name || t("adminDashboard.anonymous", "Anonymous"),
    rating: review.rating,
    text: review.comment || "",
    avatar: review.user_avatar,
  }));

  // Top courses for display
  const displayTopCourses = (topCourses || []).slice(0, 2).map((course, index) => ({
    id: course.id,
    name: course.name,
    enrollments: course.enrolled_count,
    color: index === 0 ? "green" as const : "yellow" as const,
  }));

  // Build pie chart segments
  const buildPieSegments = () => {
    if (!categories.length) return null;

    let offset = 0;
    const radius = 40;
    const circumference = 2 * Math.PI * radius;

    return categories.slice(0, 3).map((cat, index) => {
      const strokeLength = (cat.percentage / 100) * circumference;
      const strokeOffset = -offset;
      offset += strokeLength;

      const colors = [...chartSeries];
      return (
        <circle
          key={cat.category}
          cx="50" cy="50" r={radius} fill="none"
          stroke={cat.color || colors[index % colors.length]}
          strokeWidth="20"
          strokeDasharray={`${strokeLength} ${circumference}`}
          strokeDashoffset={strokeOffset}
        />
      );
    });
  };

  return (
    <div className={cx("adminDashboard")}>
      {/* Overview Stats */}
      <div className={cx("overviewStats")}>
        {statCards.map((card, index) => (
          <div
            key={index}
            className={cx("statCard", card.color, { clickable: !!card.to })}
            onClick={card.to ? () => navigate(card.to!) : undefined}
            {...(card.to ? {
              role: "link",
              tabIndex: 0,
              onKeyDown: (e: React.KeyboardEvent) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); navigate(card.to!); } },
              "aria-label": `${card.label}: ${card.value}`,
            } : {})}
          >
            <div className={cx("statIcon")}>
              {card.icon}
            </div>
            <div className={cx("statText")}>
              <p className={cx("statValue")}>{card.value}</p>
              {card.subtitle && <p className={cx("statSubtitle")}>{card.subtitle}</p>}
              <p className={cx("statLabel")}>{card.label}</p>
            </div>
            {card.to && (
              <div className={cx("statArrow")}>
                <ChevronRightIcon />
              </div>
            )}
          </div>
        ))}
      </div>

      {/* Visitors (PostHog-backed) */}
      <div className={cx("visitorsSection")}>
        <VisitorsCard />
      </div>

      {/* Content Sync */}
      <div className={cx("syncCard")}>
        <div className={cx("syncHeader")} onClick={() => setSyncExpanded(!syncExpanded)} role="button" tabIndex={0}>
          <div className={cx("syncTitleRow")}>
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" width="20" height="20">
              <path d="M23 4v6h-6" /><path d="M1 20v-6h6" />
              <path d="M3.51 9a9 9 0 0 1 14.85-3.36L23 10M1 14l4.64 4.36A9 9 0 0 0 20.49 15" />
            </svg>
            <h3 className={cx("syncTitle")}>{t("adminDashboard.syncContent", "Bidirectional Content Sync")}</h3>
          </div>
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" width="18" height="18"
            style={{ transform: syncExpanded ? "rotate(180deg)" : "none", transition: "transform 0.2s" }}>
            <polyline points="6 9 12 15 18 9" />
          </svg>
        </div>
        {syncExpanded && (
          <div className={cx("syncBody")}>
            <div className={cx("syncOptions")}>
              <label className={cx("syncOption")}>
                <input type="checkbox" checked={syncOpts.content} onChange={e => setSyncOpts(o => ({ ...o, content: e.target.checked }))} />
                <span>{t("adminDashboard.syncCourses", "Courses, Modules, Exercises")}</span>
              </label>
              <label className={cx("syncOption")}>
                <input type="checkbox" checked={syncOpts.translations} onChange={e => setSyncOpts(o => ({ ...o, translations: e.target.checked }))} />
                <span>{t("adminDashboard.syncTranslations", "Translation Cache")}</span>
              </label>
              <label className={cx("syncOption")}>
                <input type="checkbox" checked={syncOpts.media} onChange={e => setSyncOpts(o => ({ ...o, media: e.target.checked }))} />
                <span>{t("adminDashboard.syncMedia", "Media Files (S3)")}</span>
              </label>
            </div>
            <div className={cx("syncButtons")}>
              <button className={cx("syncButton", "preview")} onClick={handlePreview} disabled={syncing || previewing || (!syncOpts.content && !syncOpts.translations && !syncOpts.media)}>
                {previewing ? t("adminDashboard.previewing", "Loading Preview...") : t("adminDashboard.preview", "Preview")}
              </button>
              <button className={cx("syncButton")} onClick={handleSync} disabled={syncing || previewing || (!syncOpts.content && !syncOpts.translations && !syncOpts.media)}>
                {syncing ? t("adminDashboard.syncing", "Syncing...") : t("adminDashboard.syncNow", "Sync Now")}
              </button>
            </div>
            {syncResult && (
              <div className={cx("syncResults")}>
                <p className={cx("syncResultTitle")}>{t("adminDashboard.syncComplete", "Sync Complete")} ({(syncResult.duration_ms / 1000).toFixed(1)}s)</p>
                <div className={cx("syncResultGrid")}>
                  {syncResult.courses.total > 0 && (
                    <div className={cx("syncResultItem")}>
                      <span className={cx("syncResultLabel")}>{t("adminDashboard.courses", "Courses")}</span>
                      <span className={cx("syncResultValue")}>{syncResult.courses.total} ({syncResult.courses.dev_to_prod} → prod, {syncResult.courses.prod_to_dev} → dev)</span>
                    </div>
                  )}
                  {syncResult.modules.total > 0 && (
                    <div className={cx("syncResultItem")}>
                      <span className={cx("syncResultLabel")}>{t("adminDashboard.modules", "Modules")}</span>
                      <span className={cx("syncResultValue")}>{syncResult.modules.total} ({syncResult.modules.dev_to_prod} → prod, {syncResult.modules.prod_to_dev} → dev)</span>
                    </div>
                  )}
                  {syncResult.exercises.total > 0 && (
                    <div className={cx("syncResultItem")}>
                      <span className={cx("syncResultLabel")}>{t("adminDashboard.exercises", "Exercises")}</span>
                      <span className={cx("syncResultValue")}>{syncResult.exercises.total} ({syncResult.exercises.dev_to_prod} → prod, {syncResult.exercises.prod_to_dev} → dev)</span>
                    </div>
                  )}
                  {syncResult.categories.total > 0 && (
                    <div className={cx("syncResultItem")}>
                      <span className={cx("syncResultLabel")}>{t("adminDashboard.categories", "Categories")}</span>
                      <span className={cx("syncResultValue")}>{syncResult.categories.total} ({syncResult.categories.dev_to_prod} → prod, {syncResult.categories.prod_to_dev} → dev)</span>
                    </div>
                  )}
                  {syncResult.vocabWords.total > 0 && (
                    <div className={cx("syncResultItem")}>
                      <span className={cx("syncResultLabel")}>{t("adminDashboard.vocabWords", "Vocab Words")}</span>
                      <span className={cx("syncResultValue")}>{syncResult.vocabWords.total} ({syncResult.vocabWords.dev_to_prod} → prod, {syncResult.vocabWords.prod_to_dev} → dev)</span>
                    </div>
                  )}
                  {syncResult.translations && (
                    <div className={cx("syncResultItem")}>
                      <span className={cx("syncResultLabel")}>{t("adminDashboard.translations", "Translations")}</span>
                      <span className={cx("syncResultValue")}>
                        {syncResult.translations.words.total} {t("adminDashboard.syncWords", "words")} ({syncResult.translations.words.dev_to_prod} → prod, {syncResult.translations.words.prod_to_dev} → dev),{" "}
                        {syncResult.translations.sentences.total} {t("adminDashboard.syncSentences", "sentences")} ({syncResult.translations.sentences.dev_to_prod} → prod, {syncResult.translations.sentences.prod_to_dev} → dev)
                      </span>
                    </div>
                  )}
                  {syncResult.media && (
                    <div className={cx("syncResultItem")}>
                      <span className={cx("syncResultLabel")}>{t("adminDashboard.media", "Media")}</span>
                      <span className={cx("syncResultValue")}>{syncResult.media.copied_to_prod} → prod, {syncResult.media.copied_to_dev} → dev, {syncResult.media.skipped} {t("adminDashboard.syncSkipped", "skipped")}</span>
                    </div>
                  )}
                </div>
                {syncResult.errors.length > 0 && (
                  <div className={cx("syncErrors")}>
                    {syncResult.errors.map((err, i) => <p key={i} className={cx("syncError")}>{err}</p>)}
                  </div>
                )}
              </div>
            )}
            {previewResult && <SyncPreviewModal preview={previewResult} onConfirm={handleConfirmSync} onCancel={handleCancelPreview} syncing={syncing} t={t} />}
          </div>
        )}
      </div>

      {/* Main Content */}
      <div className={cx("dashboardContent")}>
        <div className={cx("mainColumn")}>
          {/* User Activity — unified section */}
          <div className={cx("activitySection")}>
            <div className={cx("cardHeader")}>
              <h2 className={cx("cardTitle")}>{t("adminDashboard.userActivity", "User Activity")}</h2>
              <div className={cx("tabs")} role="tablist" aria-label={t("adminDashboard.userActivity", "User Activity")}>
                {(["day", "week", "month"] as const).map(p => (
                  <button
                    key={p}
                    role="tab"
                    aria-selected={period === p}
                    className={cx("tab", { active: period === p })}
                    onClick={() => setPeriod(p)}
                  >
                    {t(`adminDashboard.${p}`)}
                  </button>
                ))}
              </div>
            </div>

            <p className={cx("activityPeriodLabel")}>
              {period === "day"
                ? t("adminDashboard.today", "Today")
                : period === "week"
                  ? t("adminDashboard.last7Days", "Last 7 days")
                  : t("adminDashboard.last30Days", "Last 30 days")}
            </p>

            {/* Activity Stat Cards */}
            {activityStatsLoading ? (
              <div className={cx("activityGrid")}>
                {[...Array(6)].map((_, i) => (
                  <div key={i} className={cx("activityCard", "skeleton")} />
                ))}
              </div>
            ) : activityStats ? (
              <div className={cx("activityGrid")}>
                {([
                  { key: "exercise_attempts", icon: <PencilIcon />, color: "blue" as const },
                  { key: "exercises_completed", icon: <CheckCircleIcon />, color: "green" as const },
                  { key: "words_added", icon: <BookOpenIcon />, color: "purple" as const },
                  { key: "learning_sessions", icon: <ZapIcon />, color: "yellow" as const },
                  { key: "course_enrollments", icon: <AwardIcon />, color: "teal" as const },
                  { key: "reviews_submitted", icon: <MessageSquareIcon />, color: "coral" as const },
                ] as const).map(({ key, icon, color }) => {
                  const stat = activityStats[key];
                  const isPositive = stat.change_percentage >= 0;
                  return (
                    <div key={key} className={cx("activityCard", color)}>
                      <div className={cx("activityCardIcon")}>
                        {icon}
                      </div>
                      <div className={cx("activityCardBody")}>
                        <p className={cx("activityCardValue")}>{stat.current.toLocaleString(i18n.language)}</p>
                        <p className={cx("activityCardLabel")}>
                          {t(`adminDashboard.${key === "exercise_attempts" ? "exerciseAttempts" : key === "exercises_completed" ? "exercisesCompleted" : key === "words_added" ? "wordsAdded" : key === "learning_sessions" ? "learningSessions" : key === "course_enrollments" ? "courseEnrollments" : "reviewsSubmitted"}`)}
                        </p>
                      </div>
                      <div className={cx("activityCardTrend", isPositive ? "positive" : "negative")}>
                        {isPositive ? <TrendUpIcon /> : <TrendDownIcon />}
                        <span>{isPositive ? "+" : ""}{stat.change_percentage}%</span>
                      </div>
                    </div>
                  );
                })}
              </div>
            ) : null}

            {/* Enrollment Chart */}
            <div className={cx("enrollmentChartBlock")}>
              <div className={cx("enrollmentChartHeader")}>
                <h3 className={cx("enrollmentChartTitle")}>{t("adminDashboard.sellingActivity", "Enrollment Activity")}</h3>
                <div className={cx("summaryStats")}>
                  <div className={cx("summaryItem")}>
                    <span className={cx("summaryLabel")}>{t("adminDashboard.currentTotal", "Current Period")}</span>
                    <span className={cx("summaryValue")}>{enrollmentActivity?.total_current ?? 0}</span>
                  </div>
                  <div className={cx("summaryItem")}>
                    <span className={cx("summaryLabel")}>{t("adminDashboard.previousTotal", "Previous Period")}</span>
                    <span className={cx("summaryValue")}>{enrollmentActivity?.total_previous ?? 0}</span>
                  </div>
                  <div className={cx("periodInfo")}>
                    <span className={cx("growthBadge", enrollmentActivity?.growth_percentage && enrollmentActivity.growth_percentage >= 0 ? "positive" : "negative")}>
                      {enrollmentActivity?.growth_percentage !== undefined
                        ? `${enrollmentActivity.growth_percentage >= 0 ? "+" : ""}${enrollmentActivity.growth_percentage}%`
                        : "+0%"}
                    </span>
                  </div>
                </div>
              </div>

              <div className={cx("legend")}>
                <div className={cx("legendItem")}>
                  <span className={cx("legendDot", "green")} />
                  <span>{t("adminDashboard.currentPeriod", "Current")}</span>
                </div>
                <div className={cx("legendItem")}>
                  <span className={cx("legendDot", "yellow")} />
                  <span>{t("adminDashboard.previousPeriod", "Previous")}</span>
                </div>
              </div>

              <div className={cx("chartArea")}>
                <div className={cx("barChart")}>
                  {chartData.length > 0 ? (
                    chartData.map((row, index) => {
                      const prevValue = prevChartData[index]?.count || 0;
                      const barWidth = (v: number) => v > 0 ? Math.max((v / maxBarValue) * 100, 3) : 0;
                      return (
                        <div key={row.period} className={cx("barRow")}>
                          <span className={cx("barLabel")}>{formatChartLabel(row.period, period)}</span>
                          <div className={cx("barsContainer")}>
                            <div className={cx("barWrapper")}>
                              <div
                                className={cx("bar", "green")}
                                style={{ width: `${barWidth(row.count)}%` }}
                              />
                              {row.count > 0 && <span className={cx("barCount", "green")}>{row.count}</span>}
                            </div>
                            <div className={cx("barWrapper")}>
                              <div
                                className={cx("bar", "yellow")}
                                style={{ width: `${barWidth(prevValue)}%` }}
                              />
                              {prevValue > 0 && <span className={cx("barCount", "yellow")}>{prevValue}</span>}
                            </div>
                          </div>
                        </div>
                      );
                    })
                  ) : (
                    <div className={cx("noDataMessage")}>
                      {t("adminDashboard.noEnrollmentData", "No enrollment data available")}
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>

          {/* User Reviews */}
          <div className={cx("userReviewsSection")}>
            <h2 className={cx("sectionTitle")}>{t("adminDashboard.userReviews", "User Reviews")}</h2>
            <div className={cx("reviewsGrid")}>
              {displayReviews.length > 0 ? (
                displayReviews.map((review) => (
                  <div key={review.id} className={cx("reviewCard")}>
                    <div className={cx("reviewUser")}>
                      {review.avatar ? (
                        <img src={review.avatar} alt={review.name} className={cx("reviewAvatar")} loading="lazy" />
                      ) : (
                        <div className={cx("reviewAvatar")} />
                      )}
                      <div className={cx("reviewUserInfo")}>
                        <p className={cx("userName")}>{review.name}</p>
                        <div className={cx("stars")}>
                          {[...Array(review.rating)].map((_, i) => (
                            <StarIcon key={i} />
                          ))}
                        </div>
                      </div>
                    </div>
                    <p className={cx("reviewText")}>{review.text || t("adminDashboard.noComment", "No comment provided")}</p>
                  </div>
                ))
              ) : (
                <div className={cx("noReviewsMessage")}>
                  {t("adminDashboard.noReviews", "No reviews yet")}
                </div>
              )}
            </div>
          </div>
        </div>

        <div className={cx("sideColumn")}>
          {/* Popular Categories */}
          <div className={cx("popularClassCard")}>
            <div className={cx("cardHeader")}>
              <h2 className={cx("cardTitle")}>{t("adminDashboard.popularClass", "Course Categories")}</h2>
              <button className={cx("dotsButton")}>
                <DotsIcon />
              </button>
            </div>

            <div className={cx("pieChartContainer")}>
              <div className={cx("pieChart")}>
                <svg viewBox="0 0 100 100" style={{ transform: "rotate(-90deg)" }}>
                  <circle cx="50" cy="50" r="40" fill="none" stroke="#DBDBDB" strokeWidth="20" />
                  {buildPieSegments()}
                </svg>
              </div>
            </div>

            <div className={cx("pieLegend")}>
              {categories.length > 0 ? (
                <>
                  {categories.slice(0, 3).map((cat, index) => (
                    <div key={cat.category} className={cx("pieLegendItem")}>
                      <div className={cx("itemLeft")}>
                        <span className={cx("dot", categoryColors[index % categoryColors.length])} />
                        <span>{cat.category} ({cat.percentage}%)</span>
                      </div>
                      <span className={cx("itemValue")}>{cat.count}</span>
                    </div>
                  ))}
                  {(() => {
                    const shownPct = categories.slice(0, 3).reduce((s, c) => s + c.percentage, 0);
                    const shownCount = categories.slice(0, 3).reduce((s, c) => s + c.count, 0);
                    const totalCount = categories.reduce((s, c) => s + c.count, 0);
                    const otherPct = Math.round(100 - shownPct);
                    const otherCount = totalCount - shownCount;
                    return otherPct > 0 ? (
                      <div className={cx("pieLegendItem")}>
                        <div className={cx("itemLeft")}>
                          <span className={cx("dot")} style={{ backgroundColor: "#DBDBDB" }} />
                          <span>{t("adminDashboard.otherCategories", "Other")} ({otherPct}%)</span>
                        </div>
                        <span className={cx("itemValue")}>{otherCount}</span>
                      </div>
                    ) : null;
                  })()}
                </>
              ) : (
                <div className={cx("noCategoriesMessage")}>
                  {t("adminDashboard.noCategories", "No category data")}
                </div>
              )}
            </div>
          </div>

          {/* Top Courses */}
          <div className={cx("topCoursesCard")}>
            <h2 className={cx("cardTitle")}>{t("adminDashboard.topCourses", "Top Courses")}</h2>

            <div className={cx("coursesList")}>
              {displayTopCourses.length > 0 ? (
                displayTopCourses.map((course, index) => (
                  <div key={course.id} className={cx("courseItem")}>
                    <div className={cx("courseIcon", course.color)}>
                      <BookIcon />
                    </div>
                    <div className={cx("courseText")}>
                      <p className={cx("courseLabel")}>{course.name}</p>
                      <p className={cx("courseValue")}>{course.enrollments.toLocaleString(i18n.language)} {t("adminDashboard.enrolled", "enrolled")}</p>
                    </div>
                    {index === 0 ? (
                      <div className={cx("miniChart")}>
                        <div className={cx("miniBar", "yellow")} style={{ height: "42px" }} />
                        <div className={cx("miniBar", "green")} style={{ height: "28px" }} />
                        <div className={cx("miniBar", "yellow")} style={{ height: "22px" }} />
                        <div className={cx("miniBar", "green")} style={{ height: "34px" }} />
                        <div className={cx("miniBar", "green")} style={{ height: "51px" }} />
                        <div className={cx("miniBar", "yellow")} style={{ height: "42px" }} />
                      </div>
                    ) : (
                      <div className={cx("progressBars")}>
                        <div className={cx("progressBar")}>
                          <div className={cx("progressFill", "green")} style={{ width: "60%" }} />
                        </div>
                        <div className={cx("progressBar")}>
                          <div className={cx("progressFill", "yellow")} style={{ width: "40%" }} />
                        </div>
                        <div className={cx("progressBar")}>
                          <div className={cx("progressFill", "green")} style={{ width: "78%" }} />
                        </div>
                      </div>
                    )}
                  </div>
                ))
              ) : (
                <div className={cx("noCoursesMessage")}>
                  {t("adminDashboard.noCourses", "No courses yet")}
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AdminDashboardPage;
